import React from "react";
import { api } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { TextInput, toast } from "@/components/admin/ui";

/* ====================================================================
   2High · Owner Cabinet — Categories Manager (Feb 2026 redesign)
   --------------------------------------------------------------------
   Renames + reorder + show/hide for the 8 locked menu categories.
   Edits stage in the form first, then commit via the explicit Save
   button so the operator always knows when changes are persisted.
   Also shows the product count per category for context.
   ==================================================================== */

const KIND_HINT = {
  wholesale:    "Quarter pound and up · operators · private pricing",
  breakdown:    "Eighths / quarters / halves of top-shelf indoor",
  edibles:      "Gummies, baked, dosed",
  dispos:       "Disposables and 510 carts",
  concentrates: "Rosin, resin, hash, badder",
  infused:      "Flower coated in rosin / kief",
  moonrocks:    "Layered, kiefed, the heaviest end of the shelf",
  flower:       "Specials / retail drops",
};

export default function Categories() {
  const [original, setOriginal] = React.useState([]);
  const [cats, setCats] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [justSavedAt, setJustSavedAt] = React.useState(0); // brief "Saved ✓" pulse
  const [expandedId, setExpandedId] = React.useState(""); // which row's copy editor is open

  const dirty = React.useMemo(
    () => JSON.stringify(cats) !== JSON.stringify(original),
    [cats, original]
  );
  const showSavedPulse = !dirty && Date.now() - justSavedAt < 2200;

  // Auto-clear the "Saved ✓" pulse after the window
  React.useEffect(() => {
    if (!justSavedAt) return;
    const t = setTimeout(() => setJustSavedAt(0), 2300);
    return () => clearTimeout(t);
  }, [justSavedAt]);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [list, products] = await Promise.all([
        api.getCategories(),
        api.listProducts(false).catch(() => []),
      ]);
      const sorted = [...list].sort((a, b) => (a.sort || 0) - (b.sort || 0));
      setCats(sorted);
      setOriginal(JSON.parse(JSON.stringify(sorted)));
      const c = {};
      (products || []).forEach((p) => { c[p.category] = (c[p.category] || 0) + 1; });
      setCounts(c);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // Warn before navigating away with unsaved edits
  React.useEffect(() => {
    function beforeUnload(e) {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  function patch(id, patchObj) {
    setCats((cur) => cur.map((c) => (c.id === id ? { ...c, ...patchObj } : c)));
  }

  function move(id, dir) {
    setCats((cur) => {
      const idx = cur.findIndex((c) => c.id === id);
      const target = idx + dir;
      if (target < 0 || target >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((c, i) => ({ ...c, sort: i + 1 }));
    });
  }

  function discard() {
    if (!dirty) return;
    if (!window.confirm("Discard all unsaved category changes?")) return;
    setCats(JSON.parse(JSON.stringify(original)));
    toast("Reverted", "warn");
  }

  async function save() {
    if (!dirty) return;
    // Validate: no blank labels
    const blank = cats.find((c) => !c.label?.trim());
    if (blank) { toast(`"${blank.id}" needs a label`, "error"); return; }
    setSaving(true);
    try {
      const payload = cats.map((c, i) => ({
        id: c.id,
        label: c.label.trim(),
        visible: c.visible !== false,
        sort: i + 1,
        // Include the editable menu copy so edits actually persist.
        // Trimmed so accidental whitespace-only edits don't replace seeded copy.
        headline:    (c.headline    || "").trim(),
        headline_em: (c.headline_em || "").trim(),
        lead:        (c.lead        || "").trim(),
      }));
      const fresh = await api.saveCategories(payload);
      const sorted = [...fresh].sort((a, b) => (a.sort || 0) - (b.sort || 0));
      // Set both refs to the SAME deep-cloned array so dirty becomes false in one paint
      const snapshot = JSON.parse(JSON.stringify(sorted));
      setCats(snapshot);
      setOriginal(snapshot);
      setJustSavedAt(Date.now());
      toast("Categories saved · live on the site", "ok");
    } catch (e) {
      toast(e.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="categories-page" className="-mx-4 lg:-mx-8 -my-4 lg:-my-8 min-h-screen px-4 lg:px-8 py-4 lg:py-8 pb-32">
      <PageHead
        eye={`Menu categories · ${cats.length} fixed`}
        title="Categories"
        sub="Rename, reorder, or hide the menu sections. Hidden categories disappear from the public Menu page."
        right={
          <span
            data-testid="cat-status"
            className={`text-[11px] tracking-[.2em] uppercase font-semibold ${dirty ? "text-[#c9a86b]" : showSavedPulse ? "text-[#5fa15a]" : "text-[#5fa15a]"}`}
          >
            ● {dirty ? "Unsaved changes" : showSavedPulse ? "Saved ✓" : "All saved"}
          </span>
        }
      />

      <div className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden max-w-4xl">
        <div className="grid grid-cols-[60px_1.4fr_0.6fr_0.6fr_120px] gap-3 items-center px-4 py-3 text-[10px] tracking-[.22em] uppercase text-[#6e6f5f] bg-[#10120c] border-b border-[#22261b]">
          <div>Order</div>
          <div>Label & ID</div>
          <div>Products</div>
          <div>Status</div>
          <div className="text-right">Reorder</div>
        </div>
        {loading && <div className="text-[#6e6f5f] text-[13px] p-6 text-center">Loading…</div>}
        {!loading && cats.map((c, i) => {
          const visible = c.visible !== false;
          const isExpanded = expandedId === c.id;
          return (
            <React.Fragment key={c.id}>
            <div
              data-testid={`category-row-${c.id}`}
              className={`grid grid-cols-[60px_1.4fr_0.6fr_0.6fr_120px] gap-3 items-center px-4 py-3 border-b border-[#22261b] last:border-b-0 transition ${
                !visible ? "opacity-60" : ""
              } ${isExpanded ? "bg-[#15180f]" : ""}`}
            >
              <div className="text-[18px] text-[#c9a86b] font-mono font-bold tabular-nums tracking-wider">
                {String(i + 1).padStart(2, "0")}
              </div>

              <div className="min-w-0">
                <TextInput
                  data-testid={`cat-label-${c.id}`}
                  value={c.label}
                  onChange={(e) => patch(c.id, { label: e.target.value })}
                  placeholder="Category label"
                  className="!py-2 !text-[14px] !font-medium"
                />
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] tracking-[.18em] uppercase font-mono text-[#6e6f5f]">id · {c.id}</span>
                  <span className="text-[10px] text-[#6e6f5f]">{KIND_HINT[c.id] || ""}</span>
                  <button
                    type="button"
                    data-testid={`cat-copy-toggle-${c.id}`}
                    onClick={() => setExpandedId(isExpanded ? "" : c.id)}
                    className={`ml-auto text-[10px] tracking-[.18em] uppercase font-mono px-2 py-1 rounded border transition ${
                      isExpanded
                        ? "border-[#c9a86b] text-[#c9a86b] bg-[#c9a86b]/10"
                        : "border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b]/50 hover:text-[#c9a86b]"
                    }`}
                  >
                    {isExpanded ? "Hide copy ▲" : "Edit menu copy ▼"}
                  </button>
                </div>
              </div>

              <div data-testid={`cat-count-${c.id}`} className="text-[13px] font-mono">
                <span className={counts[c.id] ? "text-[#a8d39d]" : "text-[#6e6f5f]"}>
                  {counts[c.id] || 0}
                </span>
              </div>

              <div>
                <button
                  type="button"
                  data-testid={`cat-vis-${c.id}`}
                  onClick={() => patch(c.id, { visible: !visible })}
                  title={visible ? "Click to hide from public site" : "Click to show on public site"}
                  className={`relative w-11 h-6 rounded-full border transition ${visible ? "bg-[#2d3a23] border-[#5fa15a]" : "bg-[#1f2218] border-[#2c3122]"}`}
                >
                  <span className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${visible ? "left-[22px] bg-[#5fa15a]" : "left-[2px] bg-[#6e6f5f]"}`} />
                </button>
                <div className={`mt-1 text-[10px] tracking-[.16em] uppercase font-mono ${visible ? "text-[#a8d39d]" : "text-[#6e6f5f]"}`}>
                  {visible ? "live" : "hidden"}
                </div>
              </div>

              <div className="flex items-center justify-end gap-1.5">
                <button
                  data-testid={`cat-up-${c.id}`}
                  onClick={() => move(c.id, -1)}
                  disabled={i === 0}
                  title="Move up"
                  className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] disabled:opacity-20 disabled:cursor-not-allowed"
                >▲</button>
                <button
                  data-testid={`cat-down-${c.id}`}
                  onClick={() => move(c.id, 1)}
                  disabled={i === cats.length - 1}
                  title="Move down"
                  className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] disabled:opacity-20 disabled:cursor-not-allowed"
                >▼</button>
              </div>
            </div>

            {isExpanded && (
              <CategoryCopyEditor cat={c} patch={patch} />
            )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-5 max-w-4xl text-[11px] text-[#6e6f5f] leading-relaxed">
        <strong className="text-[#a9aa9a]">Tip:</strong> Hiding a category removes its section from the public menu but doesn't delete any products — you can re-enable it any time. Renames apply everywhere on the site instantly after Save.
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:left-[240px] px-5 py-3 bg-[#10120c]/95 backdrop-blur border-t border-[#22261b] flex items-center justify-between gap-3">
        <div data-testid="cat-status-bar" className="text-[11px] font-mono tracking-[.06em]">
          {dirty
            ? <span className="text-[#c9a86b]">● Unsaved changes — click "Save Categories" to apply</span>
            : showSavedPulse
              ? <span className="text-[#5fa15a]">● Saved ✓ — live on the site</span>
              : <span className="text-[#5fa15a]">● All saved</span>
          }
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="cat-discard"
            onClick={discard}
            disabled={!dirty || saving}
            className="px-4 py-2.5 rounded-md border border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] text-[11px] tracking-[.2em] uppercase disabled:opacity-30 disabled:cursor-not-allowed"
          >Discard</button>
          <button
            data-testid="cat-save"
            onClick={save}
            disabled={!dirty || saving}
            className="px-5 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
          >{saving ? "Saving…" : "Save Categories"}</button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   CategoryCopyEditor — inline panel that drops below a category row when
   "Edit menu copy" is clicked. Operator edits the three pieces of public
   menu copy that appear at the top of each section on /menu:
     · Headline       (the main word, e.g. "Flower")
     · Headline accent (the gold-gradient word, e.g. "Breakdowns")
     · Lead paragraph  (small text under the headline)
   Live preview shown on the right so the operator can see exactly how it
   will read on the public site.
   ========================================================================= */
function CategoryCopyEditor({ cat, patch }) {
  const headline   = cat.headline    || "";
  const headlineEm = cat.headline_em || "";
  const lead       = cat.lead        || "";

  return (
    <div
      data-testid={`category-copy-editor-${cat.id}`}
      className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-5 px-5 py-4 border-b border-[#22261b] last:border-b-0 bg-[#10120c]/60"
    >
      {/* ---------- Inputs ---------- */}
      <div className="space-y-3">
        <div>
          <label className="block text-[10.5px] tracking-[.18em] uppercase font-mono text-[#6e6f5f] mb-1.5">
            Headline (first part)
          </label>
          <TextInput
            data-testid={`cat-headline-${cat.id}`}
            value={headline}
            onChange={(e) => patch(cat.id, { headline: e.target.value })}
            placeholder='e.g. "Flower"'
            className="!py-2"
          />
          <p className="text-[10px] text-[#6e6f5f] mt-1 leading-[1.55] normal-case">
            The main word(s) of the section title. Stays in your regular text color.
          </p>
        </div>

        <div>
          <label className="block text-[10.5px] tracking-[.18em] uppercase font-mono text-[#6e6f5f] mb-1.5">
            Headline accent (gold word)
          </label>
          <TextInput
            data-testid={`cat-headline-em-${cat.id}`}
            value={headlineEm}
            onChange={(e) => patch(cat.id, { headline_em: e.target.value })}
            placeholder='e.g. "Breakdowns"'
            className="!py-2"
          />
          <p className="text-[10px] text-[#6e6f5f] mt-1 leading-[1.55] normal-case">
            The second word that renders in the gold gradient. Leave blank if the headline is a single word.
          </p>
        </div>

        <div>
          <label className="block text-[10.5px] tracking-[.18em] uppercase font-mono text-[#6e6f5f] mb-1.5">
            Lead paragraph
          </label>
          <textarea
            data-testid={`cat-lead-${cat.id}`}
            value={lead}
            onChange={(e) => patch(cat.id, { lead: e.target.value })}
            placeholder='Short sentence under the headline (e.g. "Eighths and quarters of our top-shelf indoor — perfect for sampling the full lineup.")'
            rows={3}
            className="w-full bg-[#0a0c07] border border-[#2c3122] rounded-md px-3 py-2 text-[12.5px] text-[#c9c9a9] font-mono focus:border-[#c9a86b] outline-none resize-y"
          />
          <p className="text-[10px] text-[#6e6f5f] mt-1 leading-[1.55] normal-case">
            Shown directly under the headline. Keep to one or two sentences for maximum impact.
          </p>
        </div>
      </div>

      {/* ---------- Live preview ---------- */}
      <div className="rounded-xl border border-[#22261b] bg-[#08090c] p-5 flex flex-col gap-2">
        <div className="text-[9.5px] tracking-[.28em] uppercase font-mono text-[#6e6f5f]">
          Live preview · public /menu
        </div>
        <div className="font-mono text-[10px] tracking-[.32em] uppercase text-[#c9a86b]/85">
          — SECTION · {(cat.label || cat.id).toUpperCase()}
        </div>
        <h3
          data-testid={`cat-preview-h-${cat.id}`}
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: 38,
            lineHeight: 1.05,
            letterSpacing: "-0.012em",
            color: "#e8e4d8",
            margin: 0,
          }}
        >
          {headline || <span className="text-[#3c3c30] italic font-sans text-[15px]">(set a headline)</span>}{" "}
          {headlineEm && (
            <em
              style={{
                fontStyle: "normal",
                fontWeight: 300,
                background: "linear-gradient(110deg, #c9a86b 30%, #f1d99b 70%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >{headlineEm}</em>
          )}
        </h3>
        <p
          data-testid={`cat-preview-lead-${cat.id}`}
          className="text-[13px] leading-[1.55]"
          style={{ color: "rgba(232, 228, 216, .65)", margin: 0 }}
        >
          {lead || <span className="text-[#3c3c30] italic">(set a lead paragraph — appears under the headline)</span>}
        </p>
      </div>
    </div>
  );
}
